// @flow strict-local

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import add from 'date-fns/add';
import sub from 'date-fns/sub';

import {formatIsoDate} from 'src/utils/date-time-2';

import {TimePicker} from './time-picker.jsx';
import {
  Grid,
  CalendarHeader,
  MonthPicker,
  WeekPicker,
} from 'src/components/lib/calendar';
import {CalendarLoader} from 'src/components/lib/loading/calendar.jsx';

import css from '../booking.css';


type DatePickerProps = {
  onConfirmTimeslot: (date: string, start_time: string) => mixed,
  isAvailabilityLoading: boolean,
  selectedDate: Date,
  setSelectedDate: (Date) => mixed,
  calendarDate: Date,
  setCalendarDate: (Date) => mixed,
  eventDates: Date[],
  availabilityByDate: (
    dateKey: string,
  ) => {start_time: string, end_time: string}[],
  color: ?string,
  noAvailabilityContent?: React.Node,
  errorContent?: React.Node,
  classNames?: {wrapper?: string, calendarBox?: string},
  showConfirm?: boolean,
  timezone?: string | void,
};

export const DesktopSchedulingDatePicker = ({
  onConfirmTimeslot,
  eventDates,
  availabilityByDate,
  isAvailabilityLoading,
  setSelectedDate,
  selectedDate,
  calendarDate,
  setCalendarDate,
  color,
  noAvailabilityContent = <DefaultNoAvailability />,
  errorContent,
  classNames,
  showConfirm = true,
  timezone,
}: DatePickerProps): React.Element<'div'> => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const language = labelI18nInstance.language;
  const pickNextMonth = React.useCallback(() => {
    const nextDate = add(calendarDate, {months: 1});
    setCalendarDate(nextDate);
  }, [calendarDate]);
  const pickPreviousMonth = React.useCallback(() => {
    const nextDate = sub(calendarDate, {months: 1});
    setCalendarDate(nextDate);
  }, [calendarDate]);

  const dateAsKey = formatIsoDate(selectedDate);
  const possibleTimes = availabilityByDate(dateAsKey);

  const dateOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZoneName: timezone ? undefined : 'short',
  };

  return (
    <div
      className={
        classNames && classNames.wrapper ? classNames.wrapper : css.bookingBox
      }
    >
      <div
        className={
          classNames && classNames.calendarBox
            ? classNames.calendarBox
            : css.calendarBox
        }
      >
        {isAvailabilityLoading ? (
          <CalendarLoader />
        ) : (
          <div style={{flexFlow: 'column'}}>
            <MonthPicker
              monthIndex={calendarDate.getMonth()}
              year={calendarDate.getFullYear()}
              onPreviousMonthClick={pickPreviousMonth}
              onNextMonthClick={pickNextMonth}
              color={color}
            />
            <CalendarHeader date={calendarDate} />
            <Grid
              monthIndex={calendarDate.getMonth()}
              year={calendarDate.getFullYear()}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              eventDates={eventDates}
              color={color}
            />
          </div>
        )}
      </div>
      <div className={css.scheduleList}>
        <h2 className={css.scheduleListDate}>
          {`${selectedDate.toLocaleDateString(language, dateOptions)}${
            timezone ? ` ${t('at', 'at')} ${timezone}` : ''
          }`}
        </h2>

        {errorContent ? (
          errorContent
        ) : isAvailabilityLoading ? (
          <p className={css.scheduleDateSubtitle}>{`${t(
            'Loading_available_times',
            'Loading available times',
          )}...`}</p>
        ) : possibleTimes.length > 0 ? (
          <TimePicker
            possibleTimes={possibleTimes}
            selectedDate={selectedDate}
            onConfirmTimeslot={(timeslot) =>
              onConfirmTimeslot(dateAsKey, timeslot)
            }
            color={color}
            showConfirm={showConfirm}
          />
        ) : (
          noAvailabilityContent
        )}
      </div>
    </div>
  );
};

export const MobileSchedulingDatePicker = ({
  onConfirmTimeslot,
  eventDates,
  availabilityByDate,
  isAvailabilityLoading,
  setSelectedDate,
  selectedDate,
  calendarDate,
  setCalendarDate,
  color,
  noAvailabilityContent = <DefaultNoAvailability />,
  errorContent,
  showConfirm = true,
}: DatePickerProps): React.Element<'div'> => {
  const pickNextWeek = React.useCallback(() => {
    setCalendarDate(add(calendarDate, {weeks: 1}));
  }, [calendarDate]);
  const pickPreviousWeek = React.useCallback(() => {
    setCalendarDate(sub(calendarDate, {weeks: 1}));
  }, [calendarDate]);

  const dateAsKey = formatIsoDate(selectedDate);
  const possibleTimes = availabilityByDate(dateAsKey);

  return (
    <div className={css.mobileBookingBox}>
      <h3>
        {calendarDate.toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric',
        })}
      </h3>
      <WeekPicker
        date={calendarDate}
        selectedDate={selectedDate}
        size="small"
        setSelectedDate={setSelectedDate}
        eventDates={eventDates}
        onPreviousWeekClick={pickPreviousWeek}
        onNextWeekClick={pickNextWeek}
        monthIndex={calendarDate.getMonth()}
        color={color}
      />

      {errorContent ? (
        errorContent
      ) : isAvailabilityLoading ? (
        <CalendarLoader />
      ) : possibleTimes.length > 0 ? (
        <TimePicker
          possibleTimes={possibleTimes}
          selectedDate={selectedDate}
          onConfirmTimeslot={(timeslot) =>
            onConfirmTimeslot(dateAsKey, timeslot)
          }
          color={color}
          showConfirm={showConfirm}
        />
      ) : (
        noAvailabilityContent
      )}
    </div>
  );
};

function DefaultNoAvailability() {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <NoAvailabilityMessage>
      <p>
        {t(
          'No_times_with_shared_availability',
          'No times with shared availability',
        )}
      </p>
    </NoAvailabilityMessage>
  );
}

export function NoAvailabilityMessage({
  children,
}: {
  children: React.Node,
}): React.Node {
  return <div className={css.scheduleDateSubtitle}>{children}</div>;
}
